// TIDs, AT-URI parsing, and LiveKit room-name derivation.

const B32_SORTABLE = '234567abcdefghijklmnopqrstuvwxyz'

// atproto TID: 64 bits = 0 + 53 bits of microseconds since epoch + 10 bits
// clock id, encoded as 13 chars of sortable base32. Lexicographic order ==
// chronological order, which is what makes them usable as pagination cursors.
export function tidFromTime(usecSinceEpoch: bigint, clockId: number): string {
  let v = ((usecSinceEpoch & ((1n << 53n) - 1n)) << 10n) | BigInt(clockId & 0x3ff)
  let out = ''
  for (let i = 0; i < 13; i++) {
    out = B32_SORTABLE[Number(v & 31n)] + out
    v >>= 5n
  }
  return out
}

export function tidToTime(tid: string): bigint {
  let v = 0n
  for (const ch of tid) {
    const idx = B32_SORTABLE.indexOf(ch)
    if (idx < 0) return 0n
    v = (v << 5n) | BigInt(idx)
  }
  return v >> 10n
}

// Monotonic TID source. Seed clockId once (random 10-bit) and clamp forward so
// two messages in the same microsecond — or a clock step backwards — still get
// strictly increasing ids. Pass the highest already-issued TID (e.g. from
// storage after a restart) as the floor so new ids always sort after it.
export function makeTidSource(floorTid?: string | null): () => string {
  const clockId = Math.floor(Math.random() * 1024)
  let lastUsec = floorTid ? tidToTime(floorTid) : 0n
  return () => {
    let usec = BigInt(Date.now()) * 1000n
    if (usec <= lastUsec) usec = lastUsec + 1n
    lastUsec = usec
    return tidFromTime(usec, clockId)
  }
}

export interface AtUri {
  did: string
  collection: string
  rkey: string
}

export function parseAtUri(uri: string): AtUri | null {
  const m = /^at:\/\/(did:[a-z0-9]+:[A-Za-z0-9._:%-]+)\/([a-zA-Z0-9.-]+)\/([A-Za-z0-9._~:-]+)$/.exec(uri)
  if (!m) return null
  return { did: m[1], collection: m[2], rkey: m[3] }
}

export function makeAtUri(did: string, collection: string, rkey: string): string {
  return `at://${did}/${collection}/${rkey}`
}

// LiveKit room names are derived server-side from the channel AT-URI so a
// token can never be minted for a room the caller wasn't membership-checked
// against. Deterministic, opaque, and short enough for LiveKit's limits.
export async function roomNameForChannel(channelUri: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(channelUri))
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
  return 'hb_' + hex.slice(0, 32)
}

// Call identities are `did#deviceSlug` so one account can publish from several
// devices at once (multi-cam). Everything that needs the person, not the
// device, strips the suffix with this.
export function didOfIdentity(identity: string): string {
  const hash = identity.indexOf('#')
  return hash === -1 ? identity : identity.slice(0, hash)
}

// Identities on the audience side of a room: anonymous public viewers
// (viewer-), the recording egress watching the public page (rec-), and
// LiveKit's own egress participants (EG_). Everything else is a member
// publishing under their DID.
//
// Consent gating keys off this, so it must stay the single definition:
// treating an audience-side identity as a member would put it in the
// allow-list that lets off-stream people be subscribed to — i.e. would film
// someone who never joined the broadcast.
export function isAudienceIdentity(identity: string): boolean {
  return (
    identity.startsWith('viewer-') || identity.startsWith('rec-') || identity.startsWith('EG_')
  )
}

// Just the public: what the directory's "N watching" should count. The
// recorder is always in the room while live and is not an audience member.
export function isPublicViewer(identity: string): boolean {
  return identity.startsWith('viewer-')
}

export function randomInviteCode(): string {
  // 10 chars from an unambiguous alphabet; ~50 bits.
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789'
  const bytes = crypto.getRandomValues(new Uint8Array(10))
  return [...bytes].map((b) => alphabet[b % alphabet.length]).join('')
}
